import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown, faChevronUp, faStopwatch } from '@fortawesome/free-solid-svg-icons';

const AttendanceByEmployee = () => {
  const authState = useSelector(state => state.authReducer);
  const userId = authState.user._id;
  const userCode = authState.user.think5whyscode;

  const [fetchData, { loading }] = useFetch();
  const [attendanceData, setAttendanceData] = useState([]);

  // Filters & Sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [sortField, setSortField] = useState('startTime');
  const [sortOrder, setSortOrder] = useState('desc');

  const [expandedDates, setExpandedDates] = useState(new Set());

  const fetchAttendanceList = useCallback(() => {
    const config = {
      url: "/attendance/empcode",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId, empCode: userCode }
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response?.attendanceList) setAttendanceData(response.attendanceList);
      })
      .catch(error => console.error("Error fetching attendance:", error));
  }, [authState.token, fetchData, userId, userCode]);

  useEffect(() => {
    if (authState.isLoggedIn) fetchAttendanceList();
  }, [authState.isLoggedIn, fetchAttendanceList]);

  // Filtered & Sorted Data
  const filteredAndSortedData = useMemo(() => {
    let data = [...attendanceData];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(item =>
        item.attendanceFor?.toLowerCase().includes(term) ||
        item.remarks?.toLowerCase().includes(term)
      );
    }

    // Date filter
    if (selectedDate) {
      data = data.filter(item => 
        format(new Date(item.attendanceDate), 'yyyy-MM-dd') === selectedDate
      );
    }

    // Sorting
    data.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField.includes('Time') || sortField === 'attendanceDate') {
        valA = new Date(valA);
        valB = new Date(valB);
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [attendanceData, searchTerm, selectedDate, sortField, sortOrder]);

  // Group by Date
  const groupedAttendanceData = useMemo(() => {
    return filteredAndSortedData.reduce((acc, item) => {
      const dateKey = item.attendanceDate ? format(new Date(item.attendanceDate), 'yyyy-MM-dd') : 'Invalid';
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(item);
      return acc;
    }, {});
  }, [filteredAndSortedData]);

  const toggleExpandDate = (date) => {
    setExpandedDates(prev => {
      const newSet = new Set(prev);
      newSet.has(date) ? newSet.delete(date) : newSet.add(date);
      return newSet;
    });
  };

  const updateEndTime = (recordId) => {
    const config = {
      url: `/attendance/updateendtime`,
      method: "post",
      headers: { Authorization: authState.token },
      data: { attendanceId: recordId, endTime: new Date() }
    };

    fetchData(config).then(() => fetchAttendanceList());
  };

  const getTotalTime = (endTime, startTime) => {
    if (!endTime || !startTime) return 'N/A';
    const diffMs = new Date(endTime) - new Date(startTime);
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-gray-100">
      <header className="sticky top-0 z-50 bg-zinc-900 border-b border-yellow-500/30 px-4 py-5">
        <h1 className="text-2xl font-bold text-yellow-200 text-center">My Attendance</h1>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-zinc-900 rounded-3xl p-5 mb-8 border border-zinc-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search purpose or remarks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-11 py-4 focus:border-yellow-400"
              />
            </div>

            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-4"
            />

            <div className="flex gap-2">
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-4 flex-1"
              >
                <option value="startTime">In Time</option>
                <option value="attendanceDate">Date</option>
                <option value="attendanceFor">Purpose</option>
              </select>

              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="bg-zinc-800 border border-zinc-700 rounded-2xl px-6 font-medium"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? <Loader /> : Object.keys(groupedAttendanceData).length === 0 ? (
          <div className="text-center py-20 text-yellow-300 text-xl">
            No matching attendance records found
          </div>
        ) : (
          Object.keys(groupedAttendanceData).map(date => (
            <div key={date} className="bg-zinc-900 border border-zinc-700 rounded-3xl mb-6 overflow-hidden">
              <div 
                className="flex justify-between items-center px-6 py-5 cursor-pointer hover:bg-zinc-800"
                onClick={() => toggleExpandDate(date)}
              >
                <span className="font-semibold text-lg">{format(new Date(date), 'dd MMMM yyyy')}</span>
                <FontAwesomeIcon 
                  icon={expandedDates.has(date) ? faChevronUp : faChevronDown} 
                  className="text-yellow-400" 
                />
              </div>

              {expandedDates.has(date) && (
                <div className="px-6 pb-6 space-y-4">
                  {groupedAttendanceData[date].map(item => (
                    <div key={item._id} className="bg-zinc-800 rounded-2xl p-5">
                      <div className="flex justify-between items-start">
                        <span className="text-emerald-400 font-medium">{item.attendanceFor}</span>
                        <span className="text-gray-400 text-sm">
                          {format(new Date(item.startTime), 'HH:mm')}
                        </span>
                      </div>

                      <div className="mt-3 flex items-center justify-between text-sm">
                        <div>
                          Out: <span className="text-white">{item.endTime ? format(new Date(item.endTime), 'HH:mm') : 'Pending'}</span>
                        </div>
                        {item.endTime && (
                          <div className="text-yellow-300">
                            Total: {getTotalTime(item.endTime, item.startTime)}
                          </div>
                        )}
                      </div>

                      {!item.endTime && (
                        <button 
                          onClick={() => updateEndTime(item._id)}
                          className="mt-4 w-fit px-2 py-1 flex items-center gap-2 text-orange-400 hover:text-orange-300"
                        >
                          <FontAwesomeIcon icon={faStopwatch} /> Mark Out Time
                        </button>
                      )}

                      {item.remarks && <p className="text-gray-400 mt-4 text-sm border-t border-zinc-700 pt-3">{item.remarks}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AttendanceByEmployee;